import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import { Group, X } from 'lucide-react';
import React, { FC, useEffect, useMemo, useState } from 'react';

import { Spinner } from '../../../../components';
import { useHeroToast } from '../../../../hooks';
import {
  useAssignOrganizationToGroup,
  useGetOrganizationGroups,
} from '../../../../queries';

interface AssignToGroupModalProps {
  isOpen: boolean;
  organizationId: string;
  organizationName?: string;
  /** Pre-selected group (the org's current group). */
  currentGroupId?: string | null;
  onClose: () => void;
}

export const AssignToGroupModal: FC<AssignToGroupModalProps> = ({
  isOpen,
  organizationId,
  organizationName,
  currentGroupId,
  onClose,
}) => {
  const { showSuccess, showError } = useHeroToast();
  const { data: groups, isLoading } = useGetOrganizationGroups();
  const { mutate: assign, isPending } = useAssignOrganizationToGroup();

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(currentGroupId ?? null);

  useEffect(() => {
    if (isOpen) setSelectedGroupId(currentGroupId ?? null);
  }, [isOpen, currentGroupId]);

  const sortedGroups = useMemo(
    () => (groups ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'pl')),
    [groups],
  );

  const dirty = selectedGroupId !== (currentGroupId ?? null);

  const handleSave = () => {
    if (!dirty || isPending) return;
    assign(
      {
        organizationId,
        groupId: selectedGroupId,
        previousGroupId: currentGroupId ?? null,
      },
      {
        onSuccess: () => {
          showSuccess(selectedGroupId ? 'Przypisano do grupy' : 'Usunięto z grupy');
          onClose();
        },
        onError: (err) => {
          showError(err?.message || 'Nie udało się zaktualizować przypisania');
        },
      },
    );
  };

  const handleUnassign = () => {
    setSelectedGroupId(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => { if (!open && !isPending) onClose(); }}
      size="md"
      isDismissable={!isPending}
      scrollBehavior="inside"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <span>Przypisz do grupy</span>
              {organizationName && (
                <span className="text-xs font-normal text-gray-500">
                  Organizacja:
                  {' '}
                  <span className="font-medium text-gray-700">{organizationName}</span>
                </span>
              )}
            </ModalHeader>
            <ModalBody>
              {isLoading ? (
                <div className="py-6 flex justify-center">
                  <Spinner showLabel={false} />
                </div>
              ) : sortedGroups.length === 0 ? (
                <div className="text-center text-sm text-gray-500 py-6">
                  Nie masz jeszcze żadnych grup. Utwórz grupę w zakładce
                  {' '}
                  <span className="font-medium">Organizations Group</span>
                  .
                </div>
              ) : (
                <>
                  <div className="text-xs text-gray-500 mb-2">
                    Wybierz grupę do której organizacja ma należeć. Każda organizacja
                    może być w jednej grupie naraz.
                  </div>
                  <div className="flex flex-col gap-1.5 max-h-[320px] overflow-y-auto pr-1">
                    {sortedGroups.map((group) => {
                      const active = selectedGroupId === group.id;
                      return (
                        <button
                          key={group.id}
                          type="button"
                          onClick={() => setSelectedGroupId(group.id)}
                          className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-left transition-colors ${
                            active
                              ? 'border-primary bg-primary/5'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <div className={`w-7 h-7 rounded-md flex items-center justify-center ${active ? 'bg-primary/15 text-primary' : 'bg-gray-100 text-gray-500'}`}>
                              <Group size={14} />
                            </div>
                            <span className={`text-sm ${active ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                              {group.name || '(bez nazwy)'}
                            </span>
                          </div>
                          <Chip size="sm" variant="flat">
                            {group.assigned_organizations.length}
                          </Chip>
                        </button>
                      );
                    })}
                  </div>

                  {selectedGroupId && (
                    <button
                      type="button"
                      onClick={handleUnassign}
                      className="mt-3 flex items-center gap-1 text-xs text-gray-500 hover:text-danger"
                    >
                      <X size={12} />
                      Wyczyść wybór (nie przypisuj do żadnej grupy)
                    </button>
                  )}
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose} isDisabled={isPending}>
                Anuluj
              </Button>
              <Button
                color="primary"
                onPress={handleSave}
                isDisabled={!dirty || isPending}
                isLoading={isPending}
              >
                Zapisz
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
